import React, { memo, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setMentionIdAction } from '../../../redux/actions/collaboration';
import axios from 'axios';
import { Editor, EditorState, convertFromRaw } from 'draft-js';
import { Dialog } from '@mui/material';
import { editorDecorator } from '../utils';
import MessageMenu from './MessageMenu';
import MessageHeader from './MessageHeader';
import MessageChart from './MessageChart';
import * as MessageUI from './MessageUI';
import { HEADER_Z_INDEX } from '../../../styles/zIndexes';

interface IProps {
  message: any;
}

export default memo(function Message({ message }: IProps) {
  const dispatch = useDispatch();

  const activeTag = useSelector((state: any) => state.collaboration.activeTag);
  const highlightMessage = useSelector(
    (state: any) => state.collaboration.highlightMessage,
  );

  const setMentionId = useCallback(
    (mentionId: any) => {
      dispatch(setMentionIdAction(mentionId));
    },
    [dispatch],
  );

  const [imageData, updateimageData] = useState('');
  const [isImageClicked, toggleImage] = useState(false);
  const [mouseOnMessage, toggleMouseOnMessage] = useState(false);
  const [isMessageDeleted, setIsMessageDeleted] = useState(false);
  const profileId = localStorage.getItem('kubit:profile_id');
  const annotationUrl = message?.annotation?.url;

  useEffect(() => {
    if (annotationUrl) {
      axios
        .get(annotationUrl, { timeout: 30000 })
        .then((res) => {
          updateimageData(res.data.name);
        })
        .catch(() => {
          const url = '/images/ScreenshotFetchErrorImage.png';
          updateimageData(url);
        });
    }
  }, [annotationUrl]);

  // Remove header z-index so it is behind screenshot
  useEffect(() => {
    if (isImageClicked) {
      const header = document.getElementsByClassName('header')[0] as any;
      header.style.zIndex = 0;
      return () => {
        header.style.zIndex = HEADER_Z_INDEX;
      };
    }
  }, [isImageClicked]);

  // dummy function for Text Editor required.
  const onChangeHandler = () => {
    return 0;
  };

  // get message editor state
  const currEditorState = message.formatted
    ? EditorState.createWithContent(
        convertFromRaw(JSON.parse(message.formatted)),
        editorDecorator({ clickMention: setMentionId }),
      )
    : EditorState.createEmpty();
  const messageText = message.text;
  return (
    <MessageUI.MessageContainer
      id={`message-${message.id}`}
      onMouseEnter={() => toggleMouseOnMessage(true)}
      onMouseLeave={() => toggleMouseOnMessage(false)}
      isHighlight={highlightMessage === message.id}
    >
      {/* If message is not deleted */}
      {!isMessageDeleted && (
        <>
          <MessageHeader message={message} />
          {/* give different color for self sent message and others messages */}
          <MessageUI.MessageContent
            defaultChecked={profileId === message.creator.id}
            className="message-content"
          >
            {/* display message dropdown menu when mouse in the message */}
            {mouseOnMessage && activeTag === '' && (
              <MessageMenu
                message={message}
                isOwnMessage={profileId === message.creator.id}
                setIsMessageDeleted={setIsMessageDeleted}
              />
            )}
            {/* if the message contains chart image, display its information */}
            {annotationUrl && (
              <MessageChart
                imageData={imageData}
                message={message}
                toggleImage={toggleImage}
                isImageClicked={isImageClicked}
                hasMessageText={messageText && !!messageText.length}
              />
            )}
            {/* display message content */}
            {messageText && !!messageText.length && (
              <MessageUI.TextMessage>
                <Editor
                  editorState={currEditorState}
                  readOnly={true}
                  onChange={() => onChangeHandler()}
                />
              </MessageUI.TextMessage>
            )}
          </MessageUI.MessageContent>

          {/* display chart image with backdrop when click */}
          {isImageClicked && (
            <Dialog
              open={isImageClicked}
              maxWidth={'xl'}
              onClick={() => toggleImage(!isImageClicked)}
            >
              <MessageUI.PopupImage src={imageData} />
            </Dialog>
          )}
        </>
      )}
      {isMessageDeleted && (
        /* Deleted message. */
        <MessageUI.DeletedMessageContainer>
          <MessageUI.DeletedSeparate />
          <MessageUI.DeleteMessage>Message is deleted</MessageUI.DeleteMessage>
        </MessageUI.DeletedMessageContainer>
      )}
    </MessageUI.MessageContainer>
  );
});
